import {
	IconArrowLeft,
	IconCalendar,
	IconHistory,
	IconTag,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { ArticlePageGrid, ArticleTocPanel } from "@/components/mdx/article-toc";
import { BlogHeader } from "@/components/misc/BlogHeader";
import { MarketingAnimated } from "@/components/misc/marketing-animated";
import { MarketingContainer } from "@/components/misc/marketing-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { LegalDocumentVersion } from "@/lib/legal-versions";
import {
	formatLegalVersionDate,
	getLegalVersionLabel,
	listVersionsForDocument,
} from "@/lib/legal-versions";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationClasses,
	getStaggeredEntranceDelayStyle,
} from "@/utils/animation";

type LegalDocumentPageProps = {
	document: LegalDocumentVersion;
	allDocuments: readonly LegalDocumentVersion[];
	backHref?: string;
	backLabel?: string;
};

function VersionListItem({
	version,
	badge,
}: {
	version: LegalDocumentVersion;
	badge?: string;
}) {
	return (
		<>
			<span className="flex items-center gap-2 font-medium text-foreground">
				{getLegalVersionLabel(version)}
				{badge ? (
					<span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
						{badge}
					</span>
				) : null}
			</span>
			{version.changeSummary ? (
				<span className="text-pretty text-muted-foreground">
					{version.changeSummary}
				</span>
			) : (
				<span className="text-muted-foreground">
					Effective {formatLegalVersionDate(version.lastUpdated)}
				</span>
			)}
		</>
	);
}

export function LegalDocumentPage({
	document,
	allDocuments,
	backHref = "/legal",
	backLabel = "Back to legal directory",
}: LegalDocumentPageProps) {
	const tocItems = document.tableOfContents ?? [];
	const hasToc = tocItems.length > 0;
	const moduleLabel = document.module === "security" ? "Security" : "Legal";
	const versions = listVersionsForDocument(document.documentId, allDocuments);
	const currentVersion = versions.find((v) => v.isCurrent) ?? versions[0];
	const isArchive =
		currentVersion != null && document.versionId !== currentVersion.versionId;
	const showVersionHistory = versions.length > 1;

	return (
		<article className="pb-20 pt-12 sm:pb-24 sm:pt-16">
			<MarketingContainer>
				<MarketingAnimated delayMs={80}>
					<Button
						variant="ghost"
						size="sm"
						className="-ms-2 mb-8 rounded-full"
						onClick={() => window.location.assign(backHref)}
					>
						<IconArrowLeft className="size-4" aria-hidden />
						{backLabel}
					</Button>
				</MarketingAnimated>

				{isArchive ? (
					<MarketingAnimated delayMs={160} className="mb-8">
						<div
							className="rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-foreground sm:px-5 sm:py-4"
							role="status"
						>
							<p className="font-medium">Archived version</p>
							<p className="mt-1 text-pretty text-muted-foreground">
								You are reading the version effective{" "}
								{formatLegalVersionDate(document.lastUpdated)}. It may not
								reflect our current practices.
							</p>
							{currentVersion ? (
								<Link
									to="/legal/$slug"
									params={{ slug: document.documentId }}
									className="mt-3 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline"
								>
									View current version
								</Link>
							) : null}
						</div>
					</MarketingAnimated>
				) : null}

				<MarketingAnimated delayMs={isArchive ? 240 : 160}>
					<BlogHeader heading={document.title} description={document.summary} />
				</MarketingAnimated>

				<MarketingAnimated delayMs={320} className="mb-10 flex flex-wrap gap-3">
					<span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/25 px-3 py-1.5 text-sm text-muted-foreground">
						<IconCalendar className="size-3.5 text-primary" aria-hidden />
						{isArchive ? "Effective " : "Last updated "}
						{formatLegalVersionDate(document.lastUpdated)}
					</span>
					<span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/25 px-3 py-1.5 text-sm">
						<IconTag className="size-3.5 text-primary" aria-hidden />
						<Badge
							variant="secondary"
							className="border-0 bg-transparent px-0 font-normal"
						>
							{moduleLabel}
						</Badge>
					</span>
					{document.versionLabel ? (
						<span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
							{document.versionLabel}
						</span>
					) : null}
				</MarketingAnimated>

				{showVersionHistory ? (
					<MarketingAnimated delayMs={400} className="mb-10">
						<details
							className="group rounded-xl border border-border/80 bg-muted/15 open:bg-muted/20 dark:bg-muted/10"
							open={isArchive ? true : undefined}
						>
							<summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium text-foreground marker:content-none sm:px-5">
								<IconHistory className="size-4 text-primary" aria-hidden />
								{isArchive ? "All versions" : "View previous versions"}
								<span className="text-muted-foreground">
									({versions.length})
								</span>
							</summary>
							<ul className="space-y-1 border-t border-border/60 px-4 py-3 sm:px-5 group-open:animate-in group-open:fade-in group-open:slide-in-from-top-2 group-open:duration-300 group-open:fill-mode-both">
								{versions.map((version, index) => {
									const isActive = version.versionId === document.versionId;
									const linkClassName = cn(
										"flex flex-col gap-0.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted/40",
										isActive && "bg-primary/10 ring-1 ring-primary/20",
									);

									return (
										<li
											key={version.versionId}
											className={getStaggeredAnimationClasses(index)}
											style={getStaggeredEntranceDelayStyle(index)}
										>
											{version.isCurrent ? (
												<Link
													to="/legal/$slug"
													params={{ slug: document.documentId }}
													className={linkClassName}
													aria-current={isActive ? "page" : undefined}
												>
													<VersionListItem version={version} badge="Current" />
												</Link>
											) : (
												<Link
													to="/legal/$slug/$versionId"
													params={{
														slug: document.documentId,
														versionId: version.versionId,
													}}
													className={linkClassName}
													aria-current={isActive ? "page" : undefined}
												>
													<VersionListItem version={version} />
												</Link>
											)}
										</li>
									);
								})}
							</ul>
						</details>
					</MarketingAnimated>
				) : null}

				<ArticlePageGrid
					sidebar={
						hasToc ? (
							<ArticleTocPanel items={tocItems} variant="sidebar" />
						) : null
					}
				>
					{hasToc ? (
						<ArticleTocPanel items={tocItems} variant="inline" />
					) : null}

					<MarketingAnimated delayMs={showVersionHistory ? 480 : 400}>
						<div className="rounded-2xl border border-border/80 bg-card/60 p-6 ring-1 ring-border/40 sm:p-8 lg:p-10">
							<div
								className="legal-prose marketing-neutral-headings prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-a:text-primary"
								// biome-ignore lint/security/noDangerouslySetInnerHtml: Legal markdown is local content controlled by this repo.
								dangerouslySetInnerHTML={{ __html: document.html }}
							/>
						</div>
					</MarketingAnimated>
				</ArticlePageGrid>

				<MarketingAnimated
					delayMs={560}
					className="mt-16 rounded-2xl border border-border/80 bg-muted/15 p-8 text-center dark:bg-muted/10 sm:p-10"
				>
					<h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
						Looking for another policy?
					</h2>
					<p className="mx-auto mt-2 max-w-md text-pretty text-muted-foreground">
						Browse privacy, security, subprocessors, and other legal documents.
					</p>
					<Button
						className="mt-6 rounded-full px-6"
						size="lg"
						onClick={() => window.location.assign("/legal")}
					>
						Legal directory
						<IconArrowLeft className="size-4 rotate-180" aria-hidden />
					</Button>
				</MarketingAnimated>
			</MarketingContainer>
		</article>
	);
}
